import React from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import {
  Accordion,
  AccordionItem,
  List,
  ListItem,
  Typography
} from '@sm/wds-react';
import { createURL } from '@sm/utils';
import { defineMessages, T, t } from '@sm/intl';
import ErrorBoundary from '../../../ErrorBoundary';
import getNavBarLinks from '../../NavBar/NavBarAuthed/getNavBarLinks';
import navBarQuery from '../../NavBar/NavBarAuthed/NavBar.graphql';
import userDataQuery from '../../UserDropDown/UserDataQuery';
import {
  getProductsItems,
  getResourcesItems
} from '../../Drawer/getDrawerData';

import './mobile-menu-auth.scss';

const COPY = defineMessages({
  PRODUCTS: {
    id: 'MobileMenuAuthed.products',
    defaultMessage: 'Products',
    description:
      '[Type: Label][Vis.: high] - This is the Products title of mobile menu.'
  },
  HELP: {
    id: 'MobileMenuAuthed.help',
    defaultMessage: 'Help',
    description:
      '[Type: Label][Vis.: high] - This is the Help title of mobile menu'
  },
  SIGN_OUT: {
    id: 'MobileMenuAuthed.sign_out',
    defaultMessage: 'Sign Out',
    description:
      '[Type: Label][Vis.: high] - This is the sign out link in the mobile menu.'
  }
});

const renderAccordionItems = (accordionTitle, userLinks) => (
  <AccordionItem title={accordionTitle}>
    <List transparent>
      {userLinks.map(item => {
        const name = item.name || item.title;
        const path = item.path || item.url;
        const { openInNewWindow } = item;
        return (
          <ListItem
            key={name}
            isNavigable
            navigationTarget={path}
            {...(openInNewWindow === true && {
              target: '_blank',
              rel: 'noopener noreferrer'
            })}
          >
            <Typography
              variant="sectionTitle"
              color="link"
              className="sm-mobile-menu-anon__type-title"
            >
              {name}
            </Typography>
          </ListItem>
        );
      })}
    </List>
  </AccordionItem>
);

export const renderUserData = (username, userLinks) => (
  <Accordion className="sm-mobile-menu-auth__li-accordionitem">
    {renderAccordionItems(username, userLinks)}
  </Accordion>
);

export const getNavBarData = session => (
  <ErrorBoundary>
    <Query query={navBarQuery}>
      {({ loading, error, data }) => {
        if (loading) return null;
        if (error) throw error;

        const navBarLinks = getNavBarLinks(data, session);

        return (
          <>
            {navBarLinks.map(items => {
              if (items.name === 'My Team') {
                return (
                  <Accordion
                    key={`accordion-${items.name}`}
                    className="mobile-menu--li-accordionitem"
                  >
                    {renderAccordionItems(items.name, items.menu_items)}
                  </Accordion>
                );
              }
              if (!items.name) {
                return null;
              }
              return (
                <ListItem
                  key={items.name}
                  isNavigable
                  navigationTarget={items.path}
                >
                  <Typography
                    variant="sectionTitle"
                    color="link"
                    className="mobile-menu--type-title"
                  >
                    {items.name}
                  </Typography>
                </ListItem>
              );
            })}
          </>
        );
      }}
    </Query>
  </ErrorBoundary>
);

export const getSideMenuData = (lang, isEUDC) => {
  const productsData = getProductsItems(lang, isEUDC);
  const resourcesData = getResourcesItems(lang);
  return (
    <>
      <Accordion className="sm-mobile-menu-auth__li-accordionitem">
        {renderAccordionItems(t(COPY.PRODUCTS), productsData)}
        {renderAccordionItems(t(COPY.HELP), resourcesData)}
      </Accordion>
      <ListItem
        isNavigable
        navigationTarget={createURL('/user/sign-out/', { ut_source: 'header' })}
      >
        <Typography
          variant="sectionTitle"
          color="link"
          className="mobile-menu--type-title"
        >
          <T desc={COPY.SIGN_OUT} />
        </Typography>
      </ListItem>
    </>
  );
};
const MobileMenuAuthed = ({ lang, user, isEUDC }) => {
  return (
    <div className="sm-mobile-menu-auth__li-container">
      {userDataQuery(lang, isEUDC, renderUserData)}
      {getNavBarData(user)}
      {getSideMenuData(lang, isEUDC)}
    </div>
  );
};

MobileMenuAuthed.propTypes = {
  lang: PropTypes.string,
  user: PropTypes.shape({
    session: PropTypes.shape({
      isAdminImpersonation: PropTypes.bool
    })
  }).isRequired,
  isEUDC: PropTypes.bool.isRequired
};

MobileMenuAuthed.defaultProps = {
  lang: 'en'
};

export default MobileMenuAuthed;
